import { ReadinessModel, RouteClass, } from "../contracts/common.js";
import { expectNumber, expectObject, expectOptionalBoolean, expectOptionalNumber, expectOptionalString, expectString, parseIndexedPage, readField, readOptionalField, } from "../internal/json.js";
import { encodePathSegment, createQuery } from "../internal/query.js";
import { requestJsonResult } from "../internal/transport.js";
const BLOCKS_ROUTE = {
    method: "GET",
    name: "blocks",
    path: "/api/v1/blocks",
    readinessModel: ReadinessModel.INDEXED,
    routeClass: RouteClass.INDEXED_OVERLAY,
};
const BLOCK_ROUTE = {
    method: "GET",
    name: "block",
    path: "/api/v1/block/{hashOrHeight}",
    readinessModel: ReadinessModel.INDEXED,
    routeClass: RouteClass.INDEXED_OVERLAY,
};
const BLOCK_TXS_ROUTE = {
    method: "GET",
    name: "blockTransactions",
    path: "/api/v1/block/{hashOrHeight}/txs",
    readinessModel: ReadinessModel.INDEXED,
    routeClass: RouteClass.INDEXED,
};
/**
 * Public route descriptors for block routes.
 */
export const blockRouteDescriptors = {
    detail: BLOCK_ROUTE,
    list: BLOCKS_ROUTE,
    transactions: BLOCK_TXS_ROUTE,
};
function parseBlockTags(value, path) {
    const object = expectObject(value, path);
    return {
        orphan: expectOptionalBoolean(readOptionalField(object, "orphan"), `${path}.orphan`) ?? false,
    };
}
function parseBlockLiveTags(value, path) {
    const object = expectObject(value, path);
    const chainlock = expectOptionalBoolean(readOptionalField(object, "chainlock"), `${path}.chainlock`);
    return {
        ...(chainlock == null ? {} : { chainlock }),
    };
}
function parseBlockLive(value, path) {
    const object = expectObject(value, path);
    const tagsValue = readOptionalField(object, "tags");
    const coreHeight = expectOptionalNumber(readOptionalField(object, "coreHeight"), `${path}.coreHeight`);
    const seenAt = expectOptionalString(readOptionalField(object, "seenAt"), `${path}.seenAt`);
    return {
        ...(tagsValue == null ? {} : { tags: parseBlockLiveTags(tagsValue, `${path}.tags`) }),
        ...(coreHeight == null ? {} : { coreHeight }),
        ...(seenAt == null ? {} : { seenAt }),
    };
}
function parseCbTxFields(value, path) {
    const object = expectObject(value, path);
    const merkleRootMnList = expectOptionalString(readOptionalField(object, "merkleRootMnList"), `${path}.merkleRootMnList`);
    const merkleFlags = expectOptionalNumber(readOptionalField(object, "merkleFlags"), `${path}.merkleFlags`);
    const bestClHeight = expectOptionalNumber(readOptionalField(object, "bestClHeight"), `${path}.bestClHeight`);
    const bestClSignature = expectOptionalString(readOptionalField(object, "bestClSignature"), `${path}.bestClSignature`);
    const creditPoolBalance = expectOptionalNumber(readOptionalField(object, "creditPoolBalance"), `${path}.creditPoolBalance`);
    return {
        height: expectNumber(readField(object, "height", path), `${path}.height`),
        ...(merkleRootMnList == null ? {} : { merkleRootMnList }),
        ...(merkleFlags == null ? {} : { merkleFlags }),
        ...(bestClHeight == null ? {} : { bestClHeight }),
        ...(bestClSignature == null ? {} : { bestClSignature }),
        ...(creditPoolBalance == null ? {} : { creditPoolBalance }),
    };
}
function parseBlockSummary(value, path) {
    const object = expectObject(value, path);
    return {
        blockHeight: expectNumber(readField(object, "blockHeight", path), `${path}.blockHeight`),
        blockHash: expectString(readField(object, "blockHash", path), `${path}.blockHash`),
        prevBlockHash: expectString(readField(object, "prevBlockHash", path), `${path}.prevBlockHash`),
        blockTime: expectString(readField(object, "blockTime", path), `${path}.blockTime`),
        bits: expectString(readField(object, "bits", path), `${path}.bits`),
        nonce: expectNumber(readField(object, "nonce", path), `${path}.nonce`),
        blockVersion: expectNumber(readField(object, "blockVersion", path), `${path}.blockVersion`),
        blockSize: expectNumber(readField(object, "blockSize", path), `${path}.blockSize`),
        txCount: expectNumber(readField(object, "txCount", path), `${path}.txCount`),
        tags: parseBlockTags(readField(object, "tags", path), `${path}.tags`),
        live: parseBlockLive(readField(object, "live", path), `${path}.live`),
    };
}
function parseBlockDetail(value, path) {
    const object = expectObject(value, path);
    const orphanedAt = expectOptionalString(readOptionalField(object, "orphanedAt"), `${path}.orphanedAt`);
    const chainwork = expectOptionalString(readOptionalField(object, "chainwork"), `${path}.chainwork`);
    const cbTx = readOptionalField(object, "cbTx");
    return {
        asOfHeight: expectNumber(readField(object, "asOfHeight", path), `${path}.asOfHeight`),
        blockHeight: expectNumber(readField(object, "blockHeight", path), `${path}.blockHeight`),
        blockHash: expectString(readField(object, "blockHash", path), `${path}.blockHash`),
        prevBlockHash: expectString(readField(object, "prevBlockHash", path), `${path}.prevBlockHash`),
        merkle: expectString(readField(object, "merkle", path), `${path}.merkle`),
        blockTime: expectString(readField(object, "blockTime", path), `${path}.blockTime`),
        bits: expectString(readField(object, "bits", path), `${path}.bits`),
        nonce: expectNumber(readField(object, "nonce", path), `${path}.nonce`),
        blockVersion: expectNumber(readField(object, "blockVersion", path), `${path}.blockVersion`),
        blockSize: expectNumber(readField(object, "blockSize", path), `${path}.blockSize`),
        txCount: expectNumber(readField(object, "txCount", path), `${path}.txCount`),
        status: expectString(readField(object, "status", path), `${path}.status`),
        tags: parseBlockTags(readField(object, "tags", path), `${path}.tags`),
        live: parseBlockLive(readField(object, "live", path), `${path}.live`),
        ...(orphanedAt == null ? {} : { orphanedAt }),
        ...(chainwork == null ? {} : { chainwork }),
        ...(cbTx == null ? {} : { cbTx: parseCbTxFields(cbTx, `${path}.cbTx`) }),
    };
}
function parseBlockTxTags(value, path) {
    const object = expectObject(value, path);
    return {
        mined: expectOptionalBoolean(readOptionalField(object, "mined"), `${path}.mined`) ?? false,
        coinbase: expectOptionalBoolean(readOptionalField(object, "coinbase"), `${path}.coinbase`) ?? false,
    };
}
function parseBlockTxSummary(value, path) {
    const object = expectObject(value, path);
    return {
        txHash: expectString(readField(object, "txHash", path), `${path}.txHash`),
        blockHeight: expectNumber(readField(object, "blockHeight", path), `${path}.blockHeight`),
        blockPos: expectNumber(readField(object, "blockPos", path), `${path}.blockPos`),
        txVersion: expectNumber(readField(object, "txVersion", path), `${path}.txVersion`),
        txType: expectString(readField(object, "txType", path), `${path}.txType`),
        lockTime: expectNumber(readField(object, "lockTime", path), `${path}.lockTime`),
        txSize: expectNumber(readField(object, "txSize", path), `${path}.txSize`),
        valueOut: expectNumber(readField(object, "valueOut", path), `${path}.valueOut`),
        inputCount: expectNumber(readField(object, "inputCount", path), `${path}.inputCount`),
        outputCount: expectNumber(readField(object, "outputCount", path), `${path}.outputCount`),
        tags: parseBlockTxTags(readField(object, "tags", path), `${path}.tags`),
    };
}
function createCursorQuery(params) {
    if (params == null) {
        return undefined;
    }
    const query = createQuery({
        cursor: params.cursor,
        limit: params.limit,
    });
    return query.size > 0 ? query : undefined;
}
export function buildBlockRoutes(context) {
    return {
        async get(hashOrHeight) {
            return requestJsonResult(context, BLOCK_ROUTE, `/api/v1/block/${encodePathSegment(String(hashOrHeight))}`, parseBlockDetail);
        },
        async getTransactions(hashOrHeight, params) {
            const query = createCursorQuery(params);
            return requestJsonResult(context, BLOCK_TXS_ROUTE, `/api/v1/block/${encodePathSegment(String(hashOrHeight))}/txs`, (value, path) => parseIndexedPage(value, path, parseBlockTxSummary), ...(query == null ? [] : [{ query }]));
        },
        async list(params) {
            const query = createCursorQuery(params);
            return requestJsonResult(context, BLOCKS_ROUTE, BLOCKS_ROUTE.path, (value, path) => parseIndexedPage(value, path, parseBlockSummary), ...(query == null ? [] : [{ query }]));
        },
    };
}
//# sourceMappingURL=blocks.js.map